import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Route as RouteIcon, CalendarCheck, ArrowLeftRight, Plane, BedDouble, Scissors, Rss, Clapperboard, Radio, Users, Handshake, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WORLDS as WORLD_UI } from "@/lib/worlds";
import { SITE, worldByKey, usePageMeta } from "@/lib/site";
import { PageHeader, Section, InfoCard } from "@/components/site/SiteBits";
import { NotFound } from "@/components/SiteLinks";

const ui = (key) => WORLD_UI.find((w) => w.key === key) || {};

export function WorldCard({ w }) {
  const u = ui(w.key);
  const Icon = u.icon;
  return (
    <Link to={`/worlds/${w.key}`} className="group relative block overflow-hidden rounded-2xl border border-border bg-card p-6 card-hover" data-testid={`world-card-${w.key}`}>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: u.accentSoft, color: u.accent || w.accent }}>
        {Icon && <Icon size={24} />}
      </div>
      <h3 className="text-lg font-bold">{w.name}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{w.tagline}</p>
      <ul className="mt-4 flex flex-wrap gap-1.5">
        {w.cats.slice(0, 5).map((c) => (
          <li key={c.name} className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground/70">{c.name}</li>
        ))}
      </ul>
      <ArrowRight size={18} className="absolute right-5 top-6 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" style={{ color: u.accent || w.accent }} />
    </Link>
  );
}

export function WorldsPage() {
  usePageMeta("Six worlds", "Services, Education, Rentals, Food Market, Casting and Experiences — every local world in one Fable + account.");
  return (
    <div>
      <PageHeader kicker="Worlds" title="Six worlds, one account" lede="Each world on Fable + has a booking flow designed for how that kind of business works. A haircut needs time slots. A camera rental needs dates and a deposit. A film shoot needs money held safely until the work is done." />
      <Section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SITE.worlds.map((w) => <WorldCard key={w.key} w={w} />)}
        </div>
      </Section>
    </div>
  );
}

export function WorldPage() {
  const { key } = useParams();
  const w = worldByKey(key);
  usePageMeta(w ? `${w.name} — ${w.tagline}` : "World", w ? `${w.intro} Book on Fable +.` : undefined);
  if (!w) return <NotFound />;
  const u = ui(w.key);
  const accent = u.accent || w.accent;
  return (
    <div>
      <PageHeader kicker={`Fable + ${w.name}`} title={w.tagline} lede={w.intro} accent={accent}>
        <div className="flex flex-wrap gap-3">
          <Link to={`/marketplace?world=${w.key}`}><Button className="rounded-full px-6" style={{ background: accent }}>Browse {w.name.toLowerCase()}</Button></Link>
          <Link to="/business"><Button variant="outline" className="rounded-full px-6">Offer {w.name.toLowerCase()} on Fable +</Button></Link>
        </div>
      </PageHeader>
      <Section title="What you can book">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {w.cats.map((c) => (
            <Link key={c.name} to={`/marketplace?world=${w.key}&q=${encodeURIComponent(c.name)}`} className="rounded-2xl border border-border bg-card p-5 card-hover">
              <h3 className="font-bold">{c.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{c.body}</p>
            </Link>
          ))}
        </div>
      </Section>
      <Section alt>
        <div className="grid items-start gap-8 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: accent }}>Booking</p>
            <h2 className="mt-2 font-serif-display text-3xl font-bold">How booking works</h2>
            <p className="mt-3 text-lg text-muted-foreground">{w.booking}</p>
          </div>
          <InfoCard title="For providers" accent={accent}>
            <p>{w.lane}</p>
            <Link to="/pricing" className="mt-3 inline-block font-semibold text-primary hover:underline">See plans →</Link>
          </InfoCard>
        </div>
      </Section>
      <Section title="More worlds">
        <div className="flex flex-wrap gap-2">
          {SITE.worlds.filter((o) => o.key !== w.key).map((o) => (
            <Link key={o.key} to={`/worlds/${o.key}`} className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted" style={{ color: ui(o.key).accent || o.accent }}>
              {o.name}
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

const STAGE_ICONS = { pulse: Rss, reels: Clapperboard, live: Radio, circles: Users, collab: Handshake, backstage: KeyRound };

export function StageCard({ s }) {
  const Icon = STAGE_ICONS[s.key] || Rss;
  return (
    <div id={s.key} className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "#FFE1F0", color: "var(--fable)" }}>
        <Icon size={22} />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.line}</p>
      <h3 className="mt-1 text-lg font-bold">{s.name}</h3>
      <p className="mt-1.5 text-sm text-muted-foreground">{s.body}</p>
    </div>
  );
}

export function StagePage() {
  usePageMeta("Stage — the social side of Fable +", "Pulse, Reels, Live, Circles, Collab and Backstage: Stage is where creators post, stream, teach and team up, in the same app where you book them.");
  return (
    <div>
      <PageHeader kicker="Stage" title="Where the people behind the bookings show up." lede="Follow creators, watch them live, join their Circles and work with them, all in the same Fable + account you book with.">
        <div className="flex flex-wrap gap-3">
          <Link to="/stage/feed"><Button className="rounded-full px-6">Open the Pulse feed</Button></Link>
          <Link to="/creators"><Button variant="outline" className="rounded-full px-6">Browse creators</Button></Link>
        </div>
      </PageHeader>
      <Section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SITE.stage.map((s) => <StageCard key={s.key} s={s} />)}
        </div>
      </Section>
    </div>
  );
}

const YASMIN_POINT_ICONS = [RouteIcon, CalendarCheck, ArrowLeftRight];
const YASMIN_LINE_ICONS = { plane: Plane, bed: BedDouble, scissors: Scissors };

export function YasminChat({ cta }) {
  const h = SITE.yasmin;
  const asked = typeof window !== "undefined" ? (new URLSearchParams(window.location.search).get("q") || "").slice(0, 280) : "";
  return (
    <div className="rounded-3xl border border-border bg-card p-5 shadow-lg">
      <div className="flex items-center gap-3 border-b border-border pb-4">
        <img src="/agents/yasmin-photo.jpg?v=2" alt="Yasmin" className="h-10 w-10 rounded-full object-cover" />
        <div>
          <p className="font-bold leading-tight">Yasmin</p>
          <p className="text-xs text-emerald-600">Online</p>
        </div>
      </div>
      <div className="ml-auto mt-4 max-w-[85%] rounded-2xl rounded-br-md px-4 py-3 text-sm text-white" style={{ background: "var(--fable)" }}>{asked || h.ask}</div>
      <div className="mt-4 space-y-2">
        {h.lines.map((l) => {
          const Icon = YASMIN_LINE_ICONS[l.icon] || Plane;
          return (
            <div key={l.text} className="flex items-center gap-3 rounded-xl bg-muted px-4 py-3 text-sm font-medium">
              <Icon size={17} style={{ color: "var(--provider)" }} /> {l.text}
            </div>
          );
        })}
      </div>
      {cta}
    </div>
  );
}

export function YasminPoints() {
  return (
    <ul className="mt-6 space-y-4">
      {SITE.yasmin.points.map((p, i) => {
        const Icon = YASMIN_POINT_ICONS[i] || RouteIcon;
        return (
          <li key={p.title} className="flex gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: "#F1E4FF", color: "var(--provider)" }}><Icon size={18} /></div>
            <div>
              <p className="font-bold">{p.title}</p>
              <p className="text-sm text-muted-foreground">{p.body}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function YasminPage() {
  usePageMeta("Yasmin — your Fable + assistant", `Yasmin plans whole trips from one message, books your regular Fable + providers around them and sorts the clashes. ${SITE.yasmin.price} a month.`);
  return (
    <div>
      <PageHeader kicker="Yasmin" title="Say what you need. Yasmin plans it." lede="One message becomes flights, a hotel near the place you care about and the bookings around it, in one itinerary." accent="var(--provider)" />
      <Section>
        <div className="grid items-start gap-10 md:grid-cols-2">
          <div>
            <YasminPoints />
            <p className="mt-8 font-serif-display text-4xl font-bold">{SITE.yasmin.price}<span className="ml-2 font-sans text-base font-medium text-muted-foreground">a month, as an add-on</span></p>
            <p className="mt-4 text-sm text-muted-foreground">Yasmin suggests and you confirm: nothing is booked or paid for until you tap to confirm. Prices, times and links come from Fable + and our travel partners, not from the AI.</p>
          </div>
          <YasminChat cta={<Link to="/yasmin/chat"><Button className="mt-5 w-full rounded-full">Ask Yasmin — included in Membership</Button></Link>} />
        </div>
      </Section>
    </div>
  );
}
